import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { api, ASSET_TYPE_LABELS, ASSET_STATUS_LABELS } from '../utils/api'
import { DashboardData } from '../types'
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts'
import { AlertTriangle, Package, Monitor, RefreshCw, TrendingDown, AlertOctagon, DollarSign, ExternalLink, Building2, ChevronDown, MapPin, LayoutGrid, Headphones, Clock, CheckCircle, Inbox, AlertCircle, ShieldAlert, ArrowLeftRight, LayoutDashboard, FileText } from 'lucide-react'
import ReportsPage from './ReportsPage'

const PIE_COLORS = ['#3b82f6','#10b981','#f59e0b','#6b7280','#ef4444']
const TICKET_PRIORITY_COLORS: Record<string,string> = { CRITICAL:'#ef4444', HIGH:'#f97316', MEDIUM:'#eab308', LOW:'#6b7280' }
const TICKET_PRIORITY_LABELS: Record<string,string> = { CRITICAL:'Critical', HIGH:'High', MEDIUM:'Medium', LOW:'Low' }
const TICKET_STATUS_COLORS: Record<string,string> = { OPEN:'#3b82f6', IN_PROGRESS:'#8b5cf6', PENDING:'#f59e0b', RESOLVED:'#10b981', CLOSED:'#6b7280', CANCELLED:'#f87171' }
const TICKET_STATUS_LABELS: Record<string,string> = { OPEN:'เปิด', IN_PROGRESS:'กำลังดำเนินการ', PENDING:'รอดำเนินการ', RESOLVED:'แก้ไขแล้ว', CLOSED:'ปิด', CANCELLED:'ยกเลิก' }
const TYPE_COLORS: Record<string,string> = { LAPTOP:'#3b82f6', MONITOR:'#8b5cf6', PHONE:'#10b981', PRINTER:'#f59e0b', NETWORK:'#06b6d4', OTHER:'#6b7280' }
const fmt = (n: number) => n >= 1_000_000 ? `฿${(n/1_000_000).toFixed(1)}M` : n >= 1_000 ? `฿${(n/1_000).toFixed(0)}K` : `฿${n.toLocaleString()}`

const ClickableTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-lg px-3 py-2 text-sm">
      <p className="font-medium text-gray-900">{label || payload[0]?.name}</p>
      <p className="text-blue-600">{payload[0]?.value} assets</p>
      <p className="text-xs text-gray-400 mt-1">คลิกเพื่อดูรายการ</p>
    </div>
  )
}

function DashboardHomeTab() {
  const navigate = useNavigate()
  const [selectedCompanyId,    setSelectedCompanyId]    = useState('')
  const [selectedSiteId,       setSelectedSiteId]       = useState('')
  const [selectedDepartmentId, setSelectedDepartmentId] = useState('')
  const [licenseExpiryDays, setLicenseExpiryDays] = useState(60)

  const { data: companies = [] } = useQuery<{id:string;name:string}[]>({
    queryKey: ['masterdata','companies'],
    queryFn: () => api.get('/masterdata/companies').then(r => r.data).catch(() => []),
  })
  const { data: allSites = [] } = useQuery<{id:string;name:string;companyId?:string}[]>({
    queryKey: ['masterdata','sites'],
    queryFn: () => api.get('/masterdata/sites').then(r => r.data).catch(() => []),
  })
  const { data: allDepts = [] } = useQuery<{id:string;name:string;siteId?:string}[]>({
    queryKey: ['masterdata','departments'],
    queryFn: () => api.get('/masterdata/departments').then(r => r.data).catch(() => []),
  })

  const filteredSites = selectedCompanyId ? allSites.filter(s => s.companyId === selectedCompanyId) : allSites
  const filteredDepts = selectedSiteId    ? allDepts.filter(d => d.siteId    === selectedSiteId)    : []

  const handleCompanyChange = (val: string) => { setSelectedCompanyId(val); setSelectedSiteId(''); setSelectedDepartmentId('') }
  const handleSiteChange    = (val: string) => { setSelectedSiteId(val);    setSelectedDepartmentId('') }

  const { data: ticketStats } = useQuery({
    queryKey: ['ticket-stats-dashboard', selectedCompanyId],
    queryFn: () => {
      const p = new URLSearchParams()
      if (selectedCompanyId) p.set('companyId', selectedCompanyId)
      const q = p.toString()
      return api.get(`/tickets/stats${q ? `?${q}` : ''}`).then(r => r.data).catch(() => null)
    },
  })

  const { data, isLoading, error, refetch } = useQuery<DashboardData>({
    queryKey: ['dashboard', selectedCompanyId, selectedSiteId, selectedDepartmentId],
    queryFn: () => {
      const p = new URLSearchParams()
      if (selectedCompanyId)    p.set('companyId',    selectedCompanyId)
      if (selectedSiteId)       p.set('siteId',       selectedSiteId)
      if (selectedDepartmentId) p.set('departmentId', selectedDepartmentId)
      const q = p.toString()
      return api.get(`/dashboard${q ? `?${q}` : ''}`).then(r => r.data)
    },
    retry: 2,
  })

  const goAssets = (params: Record<string,string>) => {
    const p: Record<string,string> = { ...params }
    if (selectedCompanyId)    p.companyId    = selectedCompanyId
    if (selectedSiteId)       p.site         = selectedSiteId
    if (selectedDepartmentId) p.department   = selectedDepartmentId
    navigate(`/assets?${new URLSearchParams(p).toString()}`)
  }

  const selectedCompanyName = companies.find(c => c.id === selectedCompanyId)?.name
  const selectedSiteName    = allSites.find(s => s.id === selectedSiteId)?.name
  const selectedDeptName    = allDepts.find(d => d.id === selectedDepartmentId)?.name
  const hasFilter = selectedCompanyId || selectedSiteId || selectedDepartmentId

  if (isLoading) return (
    <div className="flex items-center justify-center h-64">
      <div className="flex flex-col items-center gap-3 text-gray-400"><RefreshCw size={24} className="animate-spin"/><p className="text-sm">กำลังโหลด...</p></div>
    </div>
  )
  if (error || !data) return (
    <div className="flex items-center justify-center h-64">
      <div className="flex flex-col items-center gap-3 text-gray-400">
        <AlertTriangle size={24} className="text-amber-400"/>
        <p className="text-sm text-gray-500">ไม่สามารถโหลด Dashboard ได้</p>
        <button onClick={() => refetch()} className="text-sm text-blue-600 hover:underline">ลองใหม่</button>
      </div>
    </div>
  )

  const statusData  = (data.byStatus||[]).map(s => ({ name: ASSET_STATUS_LABELS[s.status]||s.status, value: s._count._all, key: s.status }))
  const typeData    = (data.byType||[]).map(t => ({ name: ASSET_TYPE_LABELS[t.type]||t.type, value: t._count._all, key: t.type }))
  const deptData    = (data.byDepartment||[]).filter(d=>d.department).map(d=>({ name: d.department!, value: d._count._all })).sort((a,b)=>b.value-a.value).slice(0,10)
  const companyData = (data.byCompany||[]).filter(c=>c.company).map(c=>({ name: c.company!, value: c._count._all })).sort((a,b)=>b.value-a.value)
  const licenseCutoff = new Date(Date.now() + licenseExpiryDays * 86400000)
  const licensesExpiring = (data.licensesExpiring||[]).filter(l => l.expiryDate && new Date(l.expiryDate) <= licenseCutoff)

  const handleStatusBarClick  = (d: any) => { if (d?.activePayload?.[0]?.payload?.key) goAssets({ status: d.activePayload[0].payload.key }) }
  const handleTypeBarClick    = (d: any) => { if (d?.activePayload?.[0]?.payload?.key) goAssets({ type:   d.activePayload[0].payload.key }) }
  const handleDeptBarClick    = (d: any) => { if (d?.activePayload?.[0]?.payload?.name) goAssets({ department: d.activePayload[0].payload.name }) }
  const handleCompanyBarClick = (d: any) => { if (d?.activePayload?.[0]?.payload?.name) {
    const co = companies.find(c => c.name === d.activePayload[0].payload.name)
    if (co) navigate(`/assets?companyId=${co.id}`)
  }}
  const handlePieClick = (entry: any) => { if (entry?.key) goAssets({ status: entry.key }) }

  return (
    <div className="p-6 space-y-6">

      {/* Header + Filters */}
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
            {selectedCompanyName && (
              <span className="flex items-center gap-1 px-2.5 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
                <Building2 size={11}/> {selectedCompanyName}
              </span>
            )}
            {selectedSiteName && (
              <span className="flex items-center gap-1 px-2.5 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                <MapPin size={11}/> {selectedSiteName}
              </span>
            )}
            {selectedDeptName && (
              <span className="flex items-center gap-1 px-2.5 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                <LayoutGrid size={11}/> {selectedDeptName}
              </span>
            )}
          </div>
          <button onClick={() => refetch()} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700">
            <RefreshCw size={14}/> รีเฟรช
          </button>
        </div>

        {/* Filter dropdowns */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Company */}
          <div className="relative">
            <Building2 size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"/>
            <select value={selectedCompanyId} onChange={e => handleCompanyChange(e.target.value)}
              className="appearance-none pl-7 pr-7 py-1.5 text-sm border border-gray-200 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-300 cursor-pointer">
              <option value="">ทุกบริษัท</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"/>
          </div>
          {/* Site */}
          <div className="relative">
            <MapPin size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"/>
            <select value={selectedSiteId} onChange={e => handleSiteChange(e.target.value)}
              className="appearance-none pl-7 pr-7 py-1.5 text-sm border border-gray-200 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-300 cursor-pointer">
              <option value="">ทุก Site</option>
              {filteredSites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"/>
          </div>
          {/* Department */}
          <div className="relative">
            <LayoutGrid size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"/>
            <select value={selectedDepartmentId} onChange={e => setSelectedDepartmentId(e.target.value)}
              disabled={!selectedSiteId}
              className="appearance-none pl-7 pr-7 py-1.5 text-sm border border-gray-200 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-300 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
              <option value="">{selectedSiteId ? 'ทุก Department' : 'เลือก Site ก่อน'}</option>
              {filteredDepts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
            <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"/>
          </div>
          {hasFilter && (
            <button onClick={() => { handleCompanyChange('') }}
              className="text-xs text-gray-400 hover:text-red-500 px-2 py-1.5 rounded-lg hover:bg-red-50 transition-colors">
              ล้างตัวกรอง ✕
            </button>
          )}
        </div>
      </div>

      {/* Software License Expiry Warning */}
      {(data.licensesExpiring||[]).length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-red-200">
          <div className="flex items-center justify-between p-4 border-b border-red-100 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <ShieldAlert size={18} className="text-red-500"/>
              <h2 className="font-semibold text-gray-700">Software License ใกล้หมดอายุ</h2>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <select value={licenseExpiryDays} onChange={e => setLicenseExpiryDays(Number(e.target.value))}
                  className="appearance-none pl-3 pr-7 py-1 text-xs border border-gray-200 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-red-300 cursor-pointer">
                  <option value={60}>ภายใน 60 วัน</option>
                  <option value={180}>ภายใน 180 วัน</option>
                </select>
                <ChevronDown size={11} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"/>
              </div>
              <button onClick={() => navigate('/software')} className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                ดูทั้งหมด <ExternalLink size={11}/>
              </button>
            </div>
          </div>
          <div className="divide-y divide-gray-50">
            {licensesExpiring.length === 0 && (
              <p className="text-sm text-gray-400 text-center py-6">ไม่มี License ที่ใกล้หมดอายุภายใน {licenseExpiryDays} วัน</p>
            )}
            {licensesExpiring.map(lic => {
              const expDate = lic.expiryDate ? new Date(lic.expiryDate) : null
              const now = new Date()
              const isExpired = expDate ? expDate < now : false
              const isUrgent  = expDate ? expDate < new Date(now.getTime() + 30 * 86400000) : false
              const daysLeft  = expDate ? Math.ceil((expDate.getTime() - now.getTime()) / 86400000) : null
              return (
                <div key={lic.id} onClick={() => navigate('/software')}
                  className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-red-50 transition-colors group">
                  <ShieldAlert size={16} className={isExpired ? 'text-red-400' : isUrgent ? 'text-orange-400' : 'text-amber-400'} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate group-hover:text-blue-600">
                      {lic.name}{lic.version ? ` ${lic.version}` : ''}
                    </p>
                    <p className="text-xs text-gray-500">{lic.company||'—'}{lic.seats != null ? ` · ${lic.seats} seats` : ''}</p>
                  </div>
                  {isExpired ? (
                    <span className="text-xs bg-red-50 text-red-600 px-2 py-0.5 rounded border border-red-200 whitespace-nowrap">หมดอายุแล้ว {daysLeft != null ? `${Math.abs(daysLeft)} วันก่อน` : ''}</span>
                  ) : isUrgent ? (
                    <span className="text-xs bg-orange-50 text-orange-600 px-2 py-0.5 rounded border border-orange-200 whitespace-nowrap">เหลืออีก {daysLeft} วัน</span>
                  ) : (
                    <span className="text-xs bg-amber-50 text-amber-600 px-2 py-0.5 rounded border border-amber-200 whitespace-nowrap">เหลืออีก {daysLeft} วัน</span>
                  )}
                  <span className={`text-xs font-medium whitespace-nowrap ${isExpired ? 'text-red-600' : isUrgent ? 'text-orange-600' : 'text-amber-600'}`}>
                    {expDate ? expDate.toLocaleDateString('th-TH') : '-'}
                  </span>
                  <ExternalLink size={12} className="text-gray-300 group-hover:text-blue-400 flex-shrink-0"/>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Active Asset Loans */}
      {(data.activeBookings||[]).length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-blue-200">
          <div className="flex items-center justify-between p-4 border-b border-blue-100">
            <div className="flex items-center gap-2">
              <ArrowLeftRight size={18} className="text-blue-500"/>
              <h2 className="font-semibold text-gray-700">รายการยืม Asset ที่ยังไม่คืน</h2>
            </div>
            <button onClick={() => navigate('/assets?tab=bookings')} className="text-xs text-blue-600 hover:underline flex items-center gap-1">
              ดูทั้งหมด <ExternalLink size={11}/>
            </button>
          </div>
          <div className="divide-y divide-gray-50">
            {data.activeBookings.map(b => {
              const endDate = new Date(b.endDate)
              const now = new Date()
              const isOverdue = endDate < now
              const daysLeft  = Math.ceil((endDate.getTime() - now.getTime()) / 86400000)
              const isUrgent  = !isOverdue && daysLeft <= 2
              return (
                <div key={b.id} onClick={() => navigate('/assets?tab=bookings')}
                  className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-blue-50 transition-colors group">
                  <ArrowLeftRight size={16} className={isOverdue ? 'text-red-400' : isUrgent ? 'text-orange-400' : 'text-blue-400'} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate group-hover:text-blue-600">
                      {b.asset?.assetCode ? `[${b.asset.assetCode}] ` : ''}{b.asset?.name || b.itemName || '—'}
                      {b.status === 'PENDING' && <span className="ml-2 text-xs text-yellow-600 bg-yellow-50 px-1.5 py-0.5 rounded border border-yellow-200">รออนุมัติ</span>}
                    </p>
                    <p className="text-xs text-gray-500">{b.bookedBy||'—'}{b.purpose ? ` · ${b.purpose}` : ''}</p>
                  </div>
                  {isOverdue ? (
                    <span className="text-xs bg-red-50 text-red-600 px-2 py-0.5 rounded border border-red-200 whitespace-nowrap">เกินกำหนด {Math.abs(daysLeft)} วัน</span>
                  ) : isUrgent ? (
                    <span className="text-xs bg-orange-50 text-orange-600 px-2 py-0.5 rounded border border-orange-200 whitespace-nowrap">เหลืออีก {daysLeft} วัน</span>
                  ) : (
                    <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded border border-blue-200 whitespace-nowrap">เหลืออีก {daysLeft} วัน</span>
                  )}
                  <span className={`text-xs font-medium whitespace-nowrap ${isOverdue ? 'text-red-600' : isUrgent ? 'text-orange-600' : 'text-blue-600'}`}>
                    {endDate.toLocaleDateString('th-TH')}
                  </span>
                  <ExternalLink size={12} className="text-gray-300 group-hover:text-blue-400 flex-shrink-0"/>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div onClick={() => goAssets({})}
          className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 cursor-pointer hover:border-blue-300 hover:shadow-md transition-all group">
          <div className="flex items-center gap-2 mb-1">
            <Monitor size={16} className="text-blue-500"/>
            <p className="text-sm text-gray-500">Total Assets</p>
            <ExternalLink size={12} className="text-gray-300 group-hover:text-blue-400 ml-auto transition-colors"/>
          </div>
          <p className="text-3xl font-bold text-gray-900">{data.total}</p>
        </div>
        {(data.byStatus||[]).map(s => (
          <div key={s.status}
            onClick={() => goAssets({ status: s.status })}
            className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 cursor-pointer hover:border-blue-300 hover:shadow-md transition-all group">
            <div className="flex items-center gap-2 mb-1">
              <span className={`w-2 h-2 rounded-full ${s.status==='IN_USE'?'bg-blue-500':s.status==='AVAILABLE'?'bg-green-500':s.status==='MAINTENANCE'?'bg-yellow-500':s.status==='LOST'?'bg-red-500':'bg-gray-400'}`}/>
              <p className="text-sm text-gray-500">{ASSET_STATUS_LABELS[s.status]||s.status}</p>
              <ExternalLink size={12} className="text-gray-300 group-hover:text-blue-400 ml-auto transition-colors"/>
            </div>
            <p className="text-3xl font-bold text-gray-900">{s._count._all}</p>
          </div>
        ))}
      </div>

      {/* Financial Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-5 border border-blue-200">
          <div className="flex items-center gap-2 mb-1"><DollarSign size={16} className="text-blue-600"/><p className="text-sm text-blue-700 font-medium">มูลค่ารวม (ราคาซื้อ)</p></div>
          <p className="text-2xl font-bold text-blue-900">{fmt(data.totalPurchaseValue||0)}</p>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-5 border border-green-200">
          <div className="flex items-center gap-2 mb-1"><TrendingDown size={16} className="text-green-600"/><p className="text-sm text-green-700 font-medium">มูลค่าปัจจุบัน</p></div>
          <p className="text-2xl font-bold text-green-900">{fmt(data.totalCurrentValue||0)}</p>
        </div>
        <div onClick={() => (data.lostCount||0) > 0 && goAssets({ status: 'LOST' })}
          className={`rounded-xl p-5 border transition-all ${(data.lostCount||0)>0 ? 'bg-gradient-to-br from-red-50 to-red-100 border-red-200 cursor-pointer hover:shadow-md group' : 'bg-white border-gray-100'}`}>
          <div className="flex items-center gap-2 mb-1">
            <AlertOctagon size={16} className={(data.lostCount||0)>0?"text-red-600":"text-gray-400"}/>
            <p className={`text-sm font-medium ${(data.lostCount||0)>0?"text-red-700":"text-gray-500"}`}>Asset สูญหาย / LOST</p>
            {(data.lostCount||0) > 0 && <ExternalLink size={12} className="text-red-300 group-hover:text-red-500 ml-auto transition-colors"/>}
          </div>
          <p className={`text-2xl font-bold ${(data.lostCount||0)>0?"text-red-900":"text-gray-900"}`}>{data.lostCount||0}</p>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {statusData.length > 0 && (
          <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-1">
              <h2 className="font-semibold text-gray-700">Asset ตามสถานะ</h2>
              <span className="text-xs text-gray-400">คลิกที่กราฟเพื่อดูรายการ</span>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={statusData} cx="50%" cy="50%" outerRadius={75} dataKey="value"
                  label={({name,value})=>`${name}: ${value}`} labelLine={false}
                  cursor="pointer" onClick={handlePieClick}>
                  {statusData.map((_,i)=><Cell key={i} fill={PIE_COLORS[i%PIE_COLORS.length]}/>)}
                </Pie>
                <Tooltip content={<ClickableTooltip/>}/><Legend/>
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        {typeData.length > 0 && (
          <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-1">
              <h2 className="font-semibold text-gray-700">Asset ตามประเภท</h2>
              <span className="text-xs text-gray-400">คลิกที่แท่งเพื่อดูรายการ</span>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={typeData} margin={{top:5,right:10,left:-20,bottom:5}} onClick={handleTypeBarClick} style={{cursor:'pointer'}}>
                <XAxis dataKey="name" tick={{fontSize:11}}/><YAxis tick={{fontSize:11}} allowDecimals={false}/>
                <Tooltip content={<ClickableTooltip/>}/>
                <Bar dataKey="value" radius={[4,4,0,0]} name="จำนวน">
                  {typeData.map((entry,i) => <Cell key={i} fill={TYPE_COLORS[entry.key] || '#6b7280'}/>)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {deptData.length > 0 && (
          <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 lg:col-span-2">
            <div className="flex items-center justify-between mb-1">
              <h2 className="font-semibold text-gray-700">Asset ตามหน่วยงาน</h2>
              <span className="text-xs text-gray-400">คลิกที่แท่งเพื่อดูรายการ</span>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={deptData} margin={{top:5,right:10,left:-20,bottom:5}} onClick={handleDeptBarClick} style={{cursor:'pointer'}}>
                <XAxis dataKey="name" tick={{fontSize:11}}/><YAxis tick={{fontSize:11}} allowDecimals={false}/>
                <Tooltip content={<ClickableTooltip/>}/>
                <Bar dataKey="value" fill="#10b981" radius={[4,4,0,0]} name="จำนวน"/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Company chart — แสดงเมื่อไม่ได้ filter by company */}
        {!selectedCompanyId && companyData.length > 0 && (
          <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 lg:col-span-2">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <Building2 size={16} className="text-purple-500"/>
                <h2 className="font-semibold text-gray-700">Asset ตามบริษัท</h2>
              </div>
              <span className="text-xs text-gray-400">คลิกที่แท่งเพื่อดูรายการ</span>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={companyData} margin={{top:5,right:10,left:-20,bottom:companyData.some(c=>c.name.length>10)?40:5}} onClick={handleCompanyBarClick} style={{cursor:'pointer'}}>
                <XAxis dataKey="name" tick={{fontSize:11}} interval={0} angle={companyData.length>5?-30:0} textAnchor={companyData.length>5?"end":"middle"}/>
                <YAxis tick={{fontSize:11}} allowDecimals={false}/>
                <Tooltip content={<ClickableTooltip/>}/>
                <Bar dataKey="value" fill="#8b5cf6" radius={[4,4,0,0]} name="จำนวน"/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {statusData.length===0 && typeData.length===0 && (
          <div className="lg:col-span-2 bg-white rounded-xl p-12 shadow-sm border border-gray-100 text-center">
            <Package size={40} className="mx-auto text-gray-200 mb-3"/>
            <p className="text-gray-400">ยังไม่มีข้อมูล Asset{selectedCompanyName ? ` ของ ${selectedCompanyName}` : ''}</p>
          </div>
        )}
      </div>

      {/* ─── Ticket Summary ──────────────────────────────────────────────── */}
      {ticketStats && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Headphones size={18} className="text-blue-500"/>
              <h2 className="text-lg font-semibold text-gray-800">ServiceDesk Overview</h2>
            </div>
            <button onClick={() => navigate('/tickets')} className="text-xs text-blue-600 hover:underline flex items-center gap-1">
              ดูทั้งหมด <ExternalLink size={11}/>
            </button>
          </div>

          {/* Ticket stat cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              { label: 'เปิด',            value: ticketStats.byStatus?.find((s:any)=>s.status==='OPEN')?._count._all || 0,       icon: Inbox,        color: 'text-blue-600 bg-blue-50',   status: 'OPEN' },
              { label: 'กำลังดำเนินการ', value: ticketStats.byStatus?.find((s:any)=>s.status==='IN_PROGRESS')?._count._all || 0, icon: AlertCircle,  color: 'text-purple-600 bg-purple-50',status: 'IN_PROGRESS' },
              { label: 'รอดำเนินการ',    value: ticketStats.byStatus?.find((s:any)=>s.status==='PENDING')?._count._all || 0,     icon: Clock,        color: 'text-amber-600 bg-amber-50',  status: 'PENDING' },
              { label: 'แก้ไขแล้ว',      value: ticketStats.byStatus?.find((s:any)=>s.status==='RESOLVED')?._count._all || 0,   icon: CheckCircle,  color: 'text-green-600 bg-green-50',  status: 'RESOLVED' },
              { label: 'เกิน SLA',        value: ticketStats.overdue || 0,                                                          icon: AlertTriangle, color: 'text-red-600 bg-red-50',    status: '' },
            ].map(({ label, value, icon: Icon, color, status }) => (
              <div key={label}
                onClick={() => status ? navigate(`/tickets?status=${status}`) : navigate('/tickets')}
                className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex items-center gap-3 cursor-pointer hover:border-blue-300 hover:shadow-md transition-all group">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${color}`}><Icon size={17}/></div>
                <div>
                  <p className="text-xl font-bold text-gray-900">{value}</p>
                  <p className="text-xs text-gray-500">{label}</p>
                </div>
                <ExternalLink size={11} className="text-gray-200 group-hover:text-blue-400 ml-auto transition-colors"/>
              </div>
            ))}
          </div>

          {/* Ticket charts */}
          {((ticketStats.byPriority||[]).length > 0 || (ticketStats.byStatus||[]).length > 0) && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {(ticketStats.byPriority||[]).length > 0 && (
                <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                  <h3 className="font-semibold text-gray-700 mb-3">Tickets ตาม Priority</h3>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={(ticketStats.byPriority||[]).map((p:any) => ({ name: TICKET_PRIORITY_LABELS[p.priority]||p.priority, value: p._count._all, key: p.priority }))}
                        cx="50%" cy="50%" outerRadius={70} dataKey="value"
                        label={({name,value})=>`${name}: ${value}`} labelLine={false}
                        cursor="pointer" onClick={(entry:any) => navigate(`/tickets?priority=${entry.key}`)}>
                        {(ticketStats.byPriority||[]).map((p:any,i:number) => (
                          <Cell key={i} fill={TICKET_PRIORITY_COLORS[p.priority]||'#6b7280'}/>
                        ))}
                      </Pie>
                      <Tooltip/><Legend/>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
              {(ticketStats.byStatus||[]).length > 0 && (
                <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                  <h3 className="font-semibold text-gray-700 mb-3">Tickets ตามสถานะ</h3>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart
                      data={(ticketStats.byStatus||[]).map((s:any) => ({ name: TICKET_STATUS_LABELS[s.status]||s.status, value: s._count._all, key: s.status }))}
                      margin={{top:5,right:10,left:-20,bottom:5}}
                      onClick={(d:any) => { if (d?.activePayload?.[0]?.payload?.key) navigate(`/tickets?status=${d.activePayload[0].payload.key}`) }}
                      style={{cursor:'pointer'}}>
                      <XAxis dataKey="name" tick={{fontSize:10}}/>
                      <YAxis tick={{fontSize:11}} allowDecimals={false}/>
                      <Tooltip/>
                      <Bar dataKey="value" radius={[4,4,0,0]} name="จำนวน">
                        {(ticketStats.byStatus||[]).map((s:any,i:number) => (
                          <Cell key={i} fill={TICKET_STATUS_COLORS[s.status]||'#6b7280'}/>
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Expiry Warning */}
      {(data.warrantyExpiring||[]).length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-amber-200">
          <div className="flex items-center justify-between p-4 border-b border-amber-100">
            <div className="flex items-center gap-2">
              <AlertTriangle size={18} className="text-amber-500"/>
              <h2 className="font-semibold text-gray-700">ประกัน/สัญญาใกล้หมด (ภายใน 90 วัน)</h2>
            </div>
            <button onClick={() => goAssets({})} className="text-xs text-blue-600 hover:underline flex items-center gap-1">
              ดูทั้งหมด <ExternalLink size={11}/>
            </button>
          </div>
          <div className="divide-y divide-gray-50">
            {data.warrantyExpiring.map(asset => (
              <div key={asset.id} onClick={() => navigate(`/assets/${asset.id}`)}
                className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-amber-50 transition-colors group">
                <Package size={16} className="text-gray-400 flex-shrink-0"/>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate group-hover:text-blue-600">{asset.name}</p>
                  <p className="text-xs text-gray-500">{asset.department||'—'} · {asset.assignedTo||'—'}</p>
                </div>
                {asset.label && <span className="text-xs bg-amber-50 text-amber-600 px-2 py-0.5 rounded border border-amber-200">{asset.label}</span>}
                <span className="text-xs text-amber-600 font-medium whitespace-nowrap">
                  {asset.warrantyExpiry ? new Date(asset.warrantyExpiry).toLocaleDateString('th-TH') : '-'}
                </span>
                <ExternalLink size={12} className="text-gray-300 group-hover:text-blue-400 flex-shrink-0"/>
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  )
}

// Wrapper: "Dashboard" + "รายงาน" (Reports, moved here from its own menu item) as tabs.
// `allowedMenus` (from Settings → การเข้าถึงเมนู) still hides the Reports tab for roles without it.
export default function DashboardPage({ isAdmin = false, allowedMenus }: { isAdmin?: boolean; allowedMenus?: string[] | null }) {
  const [tab, setTab] = useState<'overview' | 'reports'>('overview')
  const showReportsTab = isAdmin || allowedMenus == null || allowedMenus.includes('reports')

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex gap-1 px-4 pt-3 border-b border-gray-200 bg-white flex-shrink-0" role="tablist">
        <button role="tab" aria-selected={tab === 'overview'} onClick={() => setTab('overview')}
          className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === 'overview' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>
          <LayoutDashboard size={14}/> Dashboard
        </button>
        {showReportsTab && (
          <button role="tab" aria-selected={tab === 'reports'} onClick={() => setTab('reports')}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === 'reports' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>
            <FileText size={14}/> รายงาน
          </button>
        )}
      </div>
      <div className="flex-1 min-h-0 overflow-auto">
        {tab === 'overview' ? <DashboardHomeTab/> : <ReportsPage isAdmin={isAdmin}/>}
      </div>
    </div>
  )
}
